const { resolveArticle, isTopLevelContext, isAutomatedClient, isPrerendering } = require('../identity/context');
const { loadScript } = require('../scriptLoader');
const { isSponsoredContent, isStaticPage } = require('../insights/pageContext');
const { getBrandConfig } = require('../../brandConfigs');
const BUNDLE_PATH = require('./bundlePath');
const controllers = new Map();
let bundlePromise;
function sessionGet(key) { try { return window.sessionStorage.getItem(key); } catch { return null; } }
function sessionSet(key, value) { try { window.sessionStorage.setItem(key, value); } catch { /* Restricted storage does not block comments. */ } }
function chooseAnchor(root, placement) {
  const explicit = root.querySelector('[data-milla-comments]'); if (explicit) return { element: explicit, kind: 'placeholder', existing: true };
  const related = root.querySelector('.all-related-sections'); if (related) return { element: related, kind: 'related', after: placement === 'below_related' };
  const body = root.querySelector('.body-description'); if (body) return { element: body.parentElement || body, kind: 'body', after: true };
  const author = root.querySelector('.social-author'); if (author) return { element: author, kind: 'author', after: true };
  return null;
}
function waitForAuth(api, restoreSession = () => Promise.resolve()) {
  if (api.getAuthState?.().authCheckComplete || api.isAuthenticated?.()) return Promise.resolve();
  return new Promise((resolve) => {
    const ready = () => { clearTimeout(timer); api.off?.('auth:ready', ready); resolve(); };
    const timer = setTimeout(ready, 1500); api.on?.('auth:ready', ready);
    // Share the SDK's in-flight refresh even when the publisher hides the auth
    // bar. The render timeout is not evidence that the reader is a guest.
    restoreSession().catch(() => {});
  });
}
function scheduleBundle(element) {
  if (bundlePromise) return bundlePromise;
  bundlePromise = new Promise((resolve, reject) => {
    let started = false; let observer; let idle;
    const start = () => { if (started) return; started = true; clearTimeout(timer); observer?.disconnect(); if (idle) window.cancelIdleCallback?.(idle); resolve(); };
    const timer = setTimeout(start, 3000);
    if (window.requestIdleCallback) idle = window.requestIdleCallback(start, { timeout: 3000 });
    if (typeof IntersectionObserver !== 'undefined') { observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) start(); }, { rootMargin: '1500px' }); observer.observe(element); }
    if (!element) reject(new Error('missing_anchor'));
  });
  return bundlePromise;
}
function setupComments(api, config, options = {}, emit = () => {}, getAccessToken = () => null, restoreSession) {
  if (api.comments) return api.comments;
  if (options.comments === false || typeof document === 'undefined' || typeof document.querySelectorAll !== 'function' || !isTopLevelContext() || isAutomatedClient() || isPrerendering()) return null;
  const brand = /(?:^|\.)mtlblog\.com$/.test(window.location.hostname) ? 'mtlblog' : /(?:^|\.)narcity\.com$/.test(window.location.hostname) ? 'narcity' : null;
  const permalink = options.comments?.permalink === true && (/^comments(?:-dev|-sandbox)?\./.test(window.location.hostname) ||
    window.location.origin === new URL(config.issuer).origin && /^\/discussions(?:\/|$)/.test(window.location.pathname));
  if (!brand || (!permalink && window.location.hostname !== `www.${brand}.com`) || !new URL(config.issuer).hostname.endsWith(`.${brand}.com`)) return null;
  // Use the CMS page classes, sections and the same sponsored context as the ad loader.
  // Static pages also expose article metadata, so resolveArticle alone is insufficient.
  if (!permalink && (document.body?.matches('.section-no-ads, .section-static-pages') ||
    isStaticPage({ staticPagesSectionId: getBrandConfig(brand).staticPagesSectionId, post_sections: window.post_sections }) ||
    isSponsoredContent({ _sponsored: window._sponsored, fwjson: window.fwjson }))) return null;
  const disabledKey = `milla-comments-disabled:${new URL(config.issuer).origin}:${brand}`;
  if (Date.now() - Number(sessionGet(disabledKey) || 0) < 600000) return null;
  const authReady = waitForAuth(api, restoreSession); let scriptPromise; let stopped = false;
  const deferred = new Map();
  const visibleArticles = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const article = deferred.get(entry.target);
      deferred.delete(entry.target); visibleArticles.unobserve(entry.target);
      if (article) mount(article).catch(() => {});
    }
  }, { rootMargin: '1500px' }) : null;
  const subject = () => {
    try { return JSON.parse(atob(getAccessToken().split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).sub || null; } catch { return null; }
  };
  const metrics = (name) => { if (options.comments?.analytics === false) return; window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: `milla_comments_${name}`, brand }); };
  const mount = async ({ articleId, article, root = document, initialData } = {}) => {
    const resolved = resolveArticle(); articleId ||= resolved?.id; article ||= resolved?.snapshot;
    if (!articleId || !article || stopped || Date.now() - Number(sessionGet(disabledKey) || 0) < 600000) return null;
    const key = `${brand}:${articleId}`; if (controllers.has(key)) return controllers.get(key).ready;
    const controller = { articleId, article, data: null, widget: null, signed: false, root, ready: null, generation: 0 };
    controllers.set(key, controller);
    const fetchThread = async ({ sort = 'newest', cursor, anonymous = false } = {}) => {
      const query = new URLSearchParams({ sort }); if (cursor) query.set('cursor', cursor);
      let focus = window.location.hash.match(/^#(?:milla-c_|c_)(cmt_[0-9A-HJKMNP-TV-Z]{26})$/)?.[1];
      try {
        const pending = JSON.parse(sessionGet(`milla-comments-return:${config.issuer}`));
        if (pending?.articleId === articleId && pending.at <= Date.now() && Date.now() - pending.at < 1800000) {
          const draft = JSON.parse(sessionGet(`milla-comments-draft:${config.issuer}:${articleId}`));
          if (/^cmt_[0-9A-HJKMNP-TV-Z]{26}$/.test(draft?.replyTargetId || '')) focus = draft.replyTargetId;
        }
      } catch { /* A missing draft does not prevent the thread from loading. */ }
      if (focus) query.set('focus', focus);
      const url = `${config.issuer}/api/comments/threads/${encodeURIComponent(articleId)}?${query}`;
      const signed = !anonymous && !!api.isAuthenticated?.();
      let response;
      for (let attempt = 0; attempt < 2; attempt++) {
        response = await (signed ? api.fetch(url, { credentials: 'include', cache: 'no-store' }) : fetch(url, { credentials: 'omit' }));
        if (response.status !== 503 || attempt) break;
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
      if (!response.ok) throw new Error('comments_unavailable');
      return response.json();
    };
    controller.refresh = async () => {
      const generation = ++controller.generation; controller.signed = !!api.isAuthenticated?.(); controller.subject = subject();
      const pageNumber = permalink ? Math.min(2500, Math.max(1, Number(new URLSearchParams(window.location.search).get('page')) || 1)) : 1;
      const data = await fetchThread({ cursor: pageNumber > 1 ? btoa(JSON.stringify({ sort: 'newest', offset: (pageNumber - 1) * 20 })).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_') : undefined });
      if (stopped || generation !== controller.generation) return;
      controller.data = data;
      if (!data.thread.id && data.sponsoredClosed && [...root.querySelectorAll('script[type="application/ld+json"]')].some((node) => {
        try { return /AdvertiserContentArticle/.test(JSON.stringify(JSON.parse(node.textContent))); } catch { return false; }
      })) data.thread.status = 'closed';
      if (!controller.signed && api.isAuthenticated?.()) return controller.refresh();
      let hasDraft = false;
      try { const saved = JSON.parse(sessionGet(`milla-comments-draft:${config.issuer}:${articleId}`)); hasDraft = !!saved?.body && Date.now() - saved.at < 86400000; } catch { /* Optional draft recovery. */ }
      if (!data.brand.enabled) { sessionSet(disabledKey, String(Date.now())); controller.widget?.destroy(); return; }
      if (!hasDraft && (data.thread.status === 'disabled' || (data.thread.status !== 'open' && data.thread.closedReason !== 'metadata_unverified' && !data.thread.counts.visible && !data.viewer?.canModerate))) {
        controller.widget?.destroy(); controller.widget = null; return;
      }
      if (controller.widget) { controller.widget.update(data); return data; }
      const anchor = chooseAnchor(root, data.thread.placement);
      if (!anchor) { metrics('mount_failed'); return; }
      const section = anchor.existing ? anchor.element : document.createElement('section');
      if (!anchor.existing) {
        section.id = controllers.size === 1 ? 'milla-comments' : `milla-comments-${articleId.replace(/\W/g, '-')}`;
        section.setAttribute('data-milla-comments', ''); section.style.minHeight = '160px';
        anchor.element.insertAdjacentElement(anchor.after ? 'afterend' : 'beforebegin', section);
      }
      if (!section.id) section.id = `milla-comments-${articleId.replace(/\W/g, '-')}`;
      await scheduleBundle(section);
      scriptPromise ||= loadScript(`${config.issuer}${BUNDLE_PATH}`); await scriptPromise;
      if (stopped || generation !== controller.generation) return;
      controller.widget = window.MillaComments.mount({ element: section, data: controller.data, article, articleId, api,
        issuer: config.issuer, fetchThread, articleRoot: permalink ? null : root, prefix: permalink ? 'c_' : 'milla-c_', emit, analytics: metrics,
        analyticsEnabled: options.comments?.analytics !== false, presenceEnabled: options.comments?.presence !== false });
      metrics(`mount_${anchor.kind}`); metrics('view'); emit('comments:ready', { articleId }); return data;
    };
    controller.ready = authReady.then(async () => {
      if (initialData && !api.isAuthenticated?.()) {
        // SSR already supplies public content; preserve it until the widget is ready.
        controller.data = initialData;
      }
      return controller.refresh();
    }).catch(() => { metrics('load_failed'); return null; });
    return controller.ready;
  };
  let scanQueued = false;
  const scan = () => {
    scanQueued = false;
    if (stopped) return;
    for (const root of deferred.keys()) if (!root.isConnected) { visibleArticles?.unobserve(root); deferred.delete(root); }
    const first = resolveArticle();
    if (first) {
      const marker = [...document.querySelectorAll('.js-scrolled-url[data-elid]')].find((node) => node.getAttribute('data-elid') === first.id);
      const root = (marker || document.querySelector('h1'))?.closest('.posts-custom, .post, .post-item, article, .article') || document;
      mount({ articleId: first.id, article: first.snapshot, root }).catch(() => {});
    }
    for (const marker of document.querySelectorAll('.js-scrolled-url[data-elid]')) {
      const id = marker.getAttribute('data-elid'); const url = marker.getAttribute('data-post-url');
      if (!id || !url || id === first?.id) continue;
      // The CMS gives each appended post its own wrapper. Never anchor a second
      // article into the first article's related-content block.
      const root = marker.closest('.posts-custom, .post, .post-item, article, .article') || marker.parentElement;
      if (!root || root === document.body) continue;
      if (controllers.has(`${brand}:${id}`) || deferred.has(root)) continue;
      const article = { articleId: id, article: { canonicalUrl: new URL(url, window.location.href).href.split(/[?#]/)[0], title: root.querySelector('h1,h2')?.textContent || '', language: document.documentElement.lang }, root };
      // Infinite scroll may append several posts far below the reader. Defer
      // the thread request itself, not just the widget's JavaScript bundle.
      if (visibleArticles) { deferred.set(root, article); visibleArticles.observe(root); }
      else mount(article).catch(() => {});
    }
  };
  if (permalink) {
    try { const stored = JSON.parse(document.getElementById('milla-comments-data').textContent); mount({ ...stored, initialData: stored.data }); } catch { /* Preserve SSR if hydration fails. */ }
  } else scan();
  const observer = typeof MutationObserver !== 'undefined' ? new MutationObserver((mutations) => {
    if (!scanQueued && mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1 && (node.matches?.('.js-scrolled-url[data-elid]') || node.querySelector?.('.js-scrolled-url[data-elid]'))))) {
      scanQueued = true; setTimeout(scan, 100);
    }
  }) : null;
  if (!permalink) observer?.observe(document.documentElement, { childList: true, subtree: true });
  const clearViewer = (controller) => { controller.generation++; if (controller.data) { delete controller.data.viewer; controller.widget?.update(controller.data); } };
  const token = () => {
    for (const controller of controllers.values()) {
      if (controller.signed && controller.subject !== subject()) { clearViewer(controller); controller.signed = false; }
      if (controller.data && !controller.signed && api.isAuthenticated?.()) controller.refresh().catch(() => {});
    }
  };
  const logout = () => { for (const controller of controllers.values()) { controller.signed = false; controller.subject = null; clearViewer(controller); } };
  api.on?.('token', token); api.on?.('logout', logout);
  const publicApi = { mount, refresh: () => Promise.all([...controllers.values()].map((c) => c.refresh())), getState: () => [...controllers.values()].map((c) => ({ articleId: c.articleId, data: c.data })),
    destroy: () => { stopped = true; observer?.disconnect(); visibleArticles?.disconnect(); deferred.clear(); api.off?.('token', token); api.off?.('logout', logout); for (const c of controllers.values()) c.widget?.destroy(); controllers.clear(); } };
  api.comments = publicApi; return publicApi;
}
module.exports = { setupComments, chooseAnchor, waitForAuth };
